<script setup lang="ts">
import { computed, watch, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useErrorStore } from '@/stores/errorStore'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useRouter } from 'vue-router'
import TheIcon from '@/components/atoms/TheIcon/TheIcon.vue'

const router = useRouter()
const errorStore = useErrorStore()
const authStore = useAuthenticationStore()
const { error } = storeToRefs(errorStore)

// User's optional additional message about the error
const userAdditionalInfo = ref('')

// Check if user is logged in
const isLoggedIn = computed(() => !!authStore.userId)

// Show error dialog when error has details
const showDetails = computed(() => {
    return error.value?.show && !!error.value?.details
})

// Reset user message when dialog closes
watch(showDetails, (show) => {
    if (!show) {
        userAdditionalInfo.value = ''
    }
    if (show) {
        document.documentElement.style.overflow = 'hidden'
    } else {
        document.documentElement.style.overflow = ''
    }
})

// Clean up on unmount
onUnmounted(() => {
    document.documentElement.style.overflow = ''
})

async function handleClose() {
    try {
        // Save redirect target BEFORE clearing error
        const targetRoute = error.value?.redirectTo || 'events-search'
        
        // Clear error (closes modal immediately)
        errorStore.clearError()
        
        // Report error with user's additional message
        await errorStore.reportError({
            source: 'error-dialog-close',
            userAdditionalInfo: userAdditionalInfo.value || undefined
        })
        
        // Redirect to configured route
        router.push({ name: targetRoute })
    } catch (err) {
        console.error('[ErrorDetailsModal] Error in handleClose:', err)
        // Still try to redirect even if something went wrong
        router.push({ name: 'events-search' })
    }
}
</script>

<template>
    <div v-if="showDetails" class="error-details-backdrop" @click="handleClose()">
        <div class="error-details-modal" @click.stop>
            <div class="error-details-modal__header">
                <h3 class="error-details-modal__title">
                    <TheIcon name="alert" color="#d9534f" class="error-details-modal__icon" />
                    Der opstod en uventet fejl
                </h3>
                <button class="error-details-modal__close" @click="handleClose()">
                    <TheIcon name="close" color="#666" />
                </button>
            </div>

            <div class="error-details-modal__content">
                <div class="error-details-modal__message">
                    <h4>En uventet fejl er desværre opstået</h4>
                    <p>Vi beklager meget. Support teamet blir automatisk underrettet når du trykker på luk og send.</p>
                </div>

                <div class="error-details-modal__info-box">
                    <p class="error-details-modal__info-text">
                        <strong>Din feedback betyder meget for os!</strong> Du kan evt. skrive dine kontakt oplysninger nedenfor, så vi kan kontakte dig for yderligere information.
                    </p>
                    <p v-if="isLoggedIn" class="error-details-modal__info-text error-details-modal__info-text--secondary">
                        Da du er logget ind, modtager vi automatisk dine brugeroplysninger, så vi kan identificere og kontakte dig hvis der er mere info vi har brug for.
                    </p>
                </div>

                <div class="error-details-modal__user-info">
                    <label for="user-message" class="error-details-modal__label">
                        Kan du fortælle os hvad du var ved at gøre? (valgfrit)
                    </label>
                    <textarea
                        id="user-message"
                        v-model="userAdditionalInfo"
                        class="error-details-modal__textarea"
                        placeholder="Beskriv kort hvad du var i gang med, når fejlen opstod..."
                        rows="3"
                    />
                </div>
            </div>

            <div class="error-details-modal__actions">
                <button 
                    class="error-details-modal__button error-details-modal__button--primary" 
                    @click="handleClose()"
                >
                    Luk og send
                </button>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.error-details-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    padding: 1rem;
}

.error-details-modal {
    background: white;
    border-radius: 8px;
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    overflow: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem;
        border-bottom: 1px solid #eee;
    }

    &__title {
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.25rem;
        color: #333;
    }

    &__icon {
        font-size: 1.5rem;
    }

    &__close {
        background: none;
        border: none;
        padding: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
            background-color: #f5f5f5;
        }
    }

    &__content {
        padding: 1.5rem;
    }

    &__message {
        margin-bottom: 2rem;
        padding: 1.5rem;
        background-color: #fef5f5;
        border-left: 4px solid #d9534f;
        border-radius: 4px;
        color: #721c24;

        h4 {
            margin: 0.5rem 0 0.75rem 0;
            font-size: 1.1rem;
            color: #721c24;
        }

        p {
            margin: 0;
            font-size: 0.95rem;
            line-height: 1.5;
        }
    }

    &__user-info {
        margin-bottom: 1.5rem;
    }

    &__label {
        display: block;
        margin-bottom: 0.75rem;
        font-weight: 500;
        color: #333;
        font-size: 0.95rem;
    }

    &__textarea {
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #ddd;
        border-radius: 4px;
        font-family: inherit;
        font-size: 0.95rem;
        resize: vertical;
        color: #333;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:focus {
            outline: none;
            border-color: #0A3D23;
            box-shadow: 0 0 0 3px rgba(10, 61, 35, 0.1);
        }

        &::placeholder {
            color: #999;
        }
    }

    &__info-box {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: #f0f8f5;
        border-left: 4px solid #0A3D23;
        border-radius: 4px;
    }

    &__info-text {
        margin: 0.5rem 0;
        font-size: 0.9rem;
        line-height: 1.5;
        color: #1a1a1a;

        &:first-child {
            margin-top: 0;
        }

        &--secondary {
            font-size: 0.85rem;
            color: #555;
            font-weight: normal;
        }
    }

    &__actions {
        padding: 1.5rem;
        border-top: 1px solid #eee;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    &__button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 4px;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.2s;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        &--primary {
            background-color: #0A3D23;
            color: white;
            font-weight: 600;
            font-size: 1rem;

            &:hover:not(:disabled) {
                background-color: #082b1a;
                box-shadow: 0 2px 8px rgba(10, 61, 35, 0.3);
            }
        }

        &-icon {
            font-size: 1rem;
        }
    }
}
</style>
